const getApiBase = () => {
  const base = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
  // If base already includes /admin, append /event. Otherwise append /admin/event.
  if (base.endsWith("/admin")) {
    return `${base}/event`;
  }
  return `${base}/admin/event`;
};

const getAuthHeaders = (isMultipart = false) => {
  const token = localStorage.getItem("token");
  const headers = {};
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  if (!isMultipart) {
    headers["Content-Type"] = "application/json";
  }
  return headers;
};

const handleResponse = async (response) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.status === false) {
    const errorMsg = data?.message || data?.error || `Request failed with status ${response.status}`;
    const err = new Error(errorMsg);
    err.response = data;
    throw err;
  }
  return data;
};

// ==========================================
// 1. Events Management
// ==========================================

export const fetchEventList = async ({ page = 1, limit = 20, status = "", search = "" } = {}) => {
  const query = new URLSearchParams();
  if (page) query.append("page", page);
  if (limit) query.append("limit", limit);
  if (status && status !== "all") query.append("status", status);
  if (search) query.append("search", search.trim());

  const url = `${getApiBase()}/list?${query.toString()}`;
  const res = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

export const createEvent = async (eventData) => {
  const url = `${getApiBase()}/create`;
  const res = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(eventData),
  });
  return handleResponse(res);
};

export const fetchEventDetails = async (id) => {
  const url = `${getApiBase()}/view/${id}`;
  const res = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

export const editEvent = async (id, eventData) => {
  const url = `${getApiBase()}/edit/${id}`;
  const res = await fetch(url, {
    method: "PUT",
    headers: getAuthHeaders(),
    body: JSON.stringify(eventData),
  });
  return handleResponse(res);
};

export const deleteEvent = async (id) => {
  const url = `${getApiBase()}/delete/${id}`;
  const res = await fetch(url, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

export const publishEvent = async (id, notifyOnPublish = false) => {
  const url = `${getApiBase()}/${id}/publish`;
  const res = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ notify_on_publish: Boolean(notifyOnPublish) }),
  });
  return handleResponse(res);
};

export const updateEventStatus = async (id, { status, reason = "", confirm = false }) => {
  const url = `${getApiBase()}/status/${id}`;
  const res = await fetch(url, {
    method: "PUT",
    headers: getAuthHeaders(),
    body: JSON.stringify({ status, reason, confirm }),
  });
  return handleResponse(res);
};

/**
 * Upload the Live Event card background. Multipart, field `image`; the API
 * switches the card to image mode and replaces any earlier image.
 */
export const uploadEventCardImage = async (eventId, file) => {
  const body = new FormData();
  body.append("image", file);

  const url = `${getApiBase()}/${eventId}/theme/card-image`;
  const res = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(true),
    body,
  });
  return handleResponse(res);
};

// ==========================================
// 2. Event Sponsors
// ==========================================

export const fetchEventSponsors = async (eventId) => {
  const url = `${getApiBase()}/${eventId}/sponsor`;
  const res = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

export const addEventSponsor = async (eventId, payload, isMultipart = false) => {
  const url = `${getApiBase()}/${eventId}/sponsor`;
  const res = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(isMultipart),
    body: isMultipart ? payload : JSON.stringify(payload),
  });
  return handleResponse(res);
};

export const updateEventSponsor = async (eventId, sponsorId, payload, isMultipart = false) => {
  const url = `${getApiBase()}/${eventId}/sponsor/${sponsorId}`;
  const res = await fetch(url, {
    method: "PUT",
    headers: getAuthHeaders(isMultipart),
    body: isMultipart ? payload : JSON.stringify(payload),
  });
  return handleResponse(res);
};

export const deleteEventSponsor = async (eventId, sponsorId) => {
  const url = `${getApiBase()}/${eventId}/sponsor/${sponsorId}`;
  const res = await fetch(url, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

// ==========================================
// 3. Prize Ladder (Tiers)
// ==========================================

export const fetchEventPrizes = async (eventId) => {
  const url = `${getApiBase()}/${eventId}/prize`;
  const res = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

export const createEventPrize = async (eventId, prizeData) => {
  const url = `${getApiBase()}/${eventId}/prize`;
  const res = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(prizeData),
  });
  return handleResponse(res);
};

export const updateEventPrize = async (eventId, prizeId, prizeData) => {
  const url = `${getApiBase()}/${eventId}/prize/${prizeId}`;
  const res = await fetch(url, {
    method: "PUT",
    headers: getAuthHeaders(),
    body: JSON.stringify(prizeData),
  });
  return handleResponse(res);
};

export const deleteEventPrize = async (eventId, prizeId) => {
  const url = `${getApiBase()}/${eventId}/prize/${prizeId}`;
  const res = await fetch(url, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

// ==========================================
// 4. Participants & Points
// ==========================================

export const fetchEventParticipants = async (eventId, { page = 1, limit = 25, search = "", isBlocked = "" } = {}) => {
  const query = new URLSearchParams();
  if (page) query.append("page", page);
  if (limit) query.append("limit", limit);
  if (search) query.append("search", search.trim());
  // "true" = only blocked, "false" = only active; empty = everyone.
  if (isBlocked === "true" || isBlocked === "false") query.append("is_blocked", isBlocked);

  const url = `${getApiBase()}/${eventId}/participants?${query.toString()}`;
  const res = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

/**
 * Task Activity: every `user_tasks` row for this event — `started` (visited,
 * never came back) and `completed` alike. See px's src/admin/task module;
 * this is the same listing, scoped by event_id server-side.
 */
export const fetchEventTaskActivity = async (eventId, { page = 1, limit = 20, taskId = "", userId = "", status = "" } = {}) => {
  const query = new URLSearchParams();
  if (page) query.append("page", page);
  if (limit) query.append("limit", limit);
  if (taskId) query.append("task_id", taskId);
  if (userId) query.append("user_id", userId);
  if (status) query.append("status", status);

  const url = `${getApiBase()}/${eventId}/task-activity?${query.toString()}`;
  const res = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

/** The monitoring view: participation, activity, leaderboard, countries, watchlist, exchange. */
export const fetchEventAnalytics = async (eventId) => {
  const url = `${getApiBase()}/${eventId}/analytics`;
  const res = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

/** Event-wide totals, split by source and by UTC day. */
export const fetchEventPointsSummary = async (eventId) => {
  const url = `${getApiBase()}/${eventId}/points-summary`;
  const res = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

export const adjustParticipantPoints = async (eventId, { user_id, points, note }) => {
  const url = `${getApiBase()}/${eventId}/adjust-points`;
  const res = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({
      user_id: user_id !== undefined && user_id !== null ? String(user_id) : "",
      points: Number(points),
      note: String(note || "").trim(),
    }),
  });
  return handleResponse(res);
};

/**
 * Block / unblock one user from this event only. A reason is required both ways.
 * Blocked users drop off the leaderboard and see 0 points; points from before the
 * block come back on unblock. Points earned while blocked are held, and the
 * unblock needs `held_points_action` ("add" | "discard") when any are held.
 */
const setEventUserBlocked = async (eventId, block, { user_id, reason, held_points_action }) => {
  const url = `${getApiBase()}/${eventId}/${block ? "block-user" : "unblock-user"}`;
  const res = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({
      user_id: user_id !== undefined && user_id !== null ? String(user_id) : "",
      reason: String(reason || "").trim(),
      ...(!block && held_points_action ? { held_points_action } : {}),
    }),
  });
  return handleResponse(res);
};

/** Points a blocked user has earned since the block, waiting for the unblock decision. */
export const fetchEventHeldPoints = async (eventId, userId) => {
  const query = new URLSearchParams({ user_id: String(userId) });
  const url = `${getApiBase()}/${eventId}/held-points?${query.toString()}`;
  const res = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

export const blockEventUser = (eventId, payload) => setEventUserBlocked(eventId, true, payload);
export const unblockEventUser = (eventId, payload) => setEventUserBlocked(eventId, false, payload);

/** Every block/unblock on this event, newest first. Pass `userId` for one user. */
export const fetchEventBlockLog = async (eventId, { page = 1, limit = 25, userId = "" } = {}) => {
  const query = new URLSearchParams();
  if (page) query.append("page", page);
  if (limit) query.append("limit", limit);
  if (userId) query.append("user_id", userId);

  const url = `${getApiBase()}/${eventId}/block-log?${query.toString()}`;
  const res = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

export const recomputeEventRanks = async (eventId) => {
  const url = `${getApiBase()}/${eventId}/recompute-ranks`;
  const res = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

// ==========================================
// 5. Finalization, Payouts & Winners
// ==========================================

export const finalizeEvent = async (eventId) => {
  const url = `${getApiBase()}/${eventId}/finalize`;
  const res = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

/**
 * The winners queue, one page at a time.
 *
 * `search`, `reward` ("px" | "crypto") and `payout_status` ("pending" | "paid")
 * are applied by the API over every winner in the event, so a search finds a
 * winner on any page, not just the one on screen.
 */
export const fetchEventWinners = async (
  eventId,
  { page = 1, limit = 50, prize_status = "", search = "", reward = "", payout_status = "" } = {}
) => {
  const query = new URLSearchParams();
  if (page) query.append("page", page);
  if (limit) query.append("limit", limit);
  if (prize_status && prize_status !== "all") query.append("prize_status", prize_status);
  if (search && search.trim()) query.append("search", search.trim());
  if (reward && reward !== "all") query.append("reward", reward);
  if (payout_status) query.append("payout_status", payout_status);

  const url = `${getApiBase()}/${eventId}/winners?${query.toString()}`;
  const res = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

/**
 * Record that one bonus of a prize has been sent.
 *
 * A prize is a list of currencies and they go out separately, so `currency`
 * names which one is being approved. Omitting it marks every outstanding
 * currency at once, which is only right when they really did go together.
 *
 * `reference` is the transaction id — the hash, the payment id, or whatever the
 * transfer can be pointed at when a winner asks about it later. The API stores
 * it on the payout row and hands it back with the winner.
 */
export const markWinnerPaid = async (eventId, userId, { currency, reference, note } = {}) => {
  const cleanUserId = userId !== undefined && userId !== null ? encodeURIComponent(String(userId)) : "";
  const url = `${getApiBase()}/${eventId}/winners/${cleanUserId}/mark-paid`;
  const res = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({
      ...(currency ? { currency } : {}),
      ...(reference ? { reference } : {}),
      ...(note ? { note } : {}),
    }),
  });
  return handleResponse(res);
};

/** @deprecated the name predates a prize having more than one currency. */
export const markWinnerUsdtPaid = markWinnerPaid;

export const fetchEventReconciliation = async (eventId) => {
  const url = `${getApiBase()}/${eventId}/reconcile`;
  const res = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

/**
 * Take an approved reward back to pending.
 *
 * For PX this debits the credited amount from the winner's balance (the API
 * refuses if their balance no longer covers it). For other currencies it undoes
 * the record. The reason is required and is what the activity log shows; the
 * cleared transaction id survives only there.
 */
export const revertWinnerPayout = async (eventId, userId, { currency, reason } = {}) => {
  const cleanUserId = userId !== undefined && userId !== null ? encodeURIComponent(String(userId)) : "";
  const url = `${getApiBase()}/${eventId}/winners/${cleanUserId}/revert`;
  const res = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ ...(currency ? { currency } : {}), reason: String(reason || "").trim() }),
  });
  return handleResponse(res);
};

/**
 * Approve PX for many winners in one request — every pending winner in the
 * event, or only `userIds` when given. Runs server-side, so it covers every
 * page of the queue, not only the one on screen.
 *
 * `includeNotCredited` also credits PX that was marked approved before approval
 * credited the balance (those winners never got their coins).
 */
export const bulkApproveWinners = async (eventId, { currency = "PX", userIds, includeNotCredited, note } = {}) => {
  const url = `${getApiBase()}/${eventId}/winners/bulk-approve`;
  const res = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({
      currency,
      ...(Array.isArray(userIds) && userIds.length ? { user_ids: userIds } : {}),
      ...(includeNotCredited ? { include_not_credited: true } : {}),
      ...(note ? { note } : {}),
    }),
  });
  return handleResponse(res);
};

/**
 * Revert approved rewards for many winners in one request — every approved
 * winner for that currency, or only `userIds`. PX is debited back from each
 * balance; a winner whose balance no longer covers it is skipped and listed in
 * `data.failed`.
 */
export const bulkRevertWinners = async (eventId, { currency = "PX", userIds, reason } = {}) => {
  const url = `${getApiBase()}/${eventId}/winners/bulk-revert`;
  const res = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({
      currency,
      reason: String(reason || "").trim(),
      ...(Array.isArray(userIds) && userIds.length ? { user_ids: userIds } : {}),
    }),
  });
  return handleResponse(res);
};

/**
 * USDT email 1: ask winners to reply with their Binance UID and registered
 * email. Every pending winner of `currency` who has not been emailed yet, or
 * only `userIds`; `resend` includes those already emailed.
 */
export const emailWinnerDetails = async (eventId, { currency = "USDT", userIds, resend } = {}) => {
  const url = `${getApiBase()}/${eventId}/winners/email-details`;
  const res = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({
      currency,
      ...(Array.isArray(userIds) && userIds.length ? { user_ids: userIds } : {}),
      ...(resend ? { resend: true } : {}),
    }),
  });
  return handleResponse(res);
};

/**
 * USDT email 2, again: the transfer confirmation with its order id. It is sent
 * automatically on approval; this is for one that failed or went missing.
 */
export const resendWinnerConfirmation = async (eventId, userId, { currency = "USDT" } = {}) => {
  const cleanUserId = userId !== undefined && userId !== null ? encodeURIComponent(String(userId)) : "";
  const url = `${getApiBase()}/${eventId}/winners/${cleanUserId}/email-confirmation`;
  const res = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ currency }),
  });
  return handleResponse(res);
};

/** One event's history, newest first. */
export const fetchEventActivity = async (eventId, { page = 1, limit = 50, action = "" } = {}) => {
  const query = new URLSearchParams();
  if (page) query.append("page", page);
  if (limit) query.append("limit", limit);
  if (action) query.append("action", action);

  const url = `${getApiBase()}/${eventId}/activity?${query.toString()}`;
  const res = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

// ==========================================
// 8. Points History  (read-only)
// ==========================================

/**
 * Where one participant's points came from, and when.
 *
 * Returns two views of the same total in one payload, because the backend records
 * it at two resolutions on purpose:
 *
 *   `daily`  — every source, per UTC day. Always complete; the only record of
 *              mining, streak and referral.
 *   `ledger` — the discrete awards to the second: check-ins, Creator Hub
 *              milestones, admin adjustments, prizes. Keyset-paged.
 *
 * `userId` may be the public uuid or the numeric id.
 */
export const fetchParticipantHistory = async (
  eventId,
  userId,
  { from = "", to = "", source = "", cursor = "", limit = 50 } = {}
) => {
  const query = new URLSearchParams();
  if (from) query.append("from", from);
  if (to) query.append("to", to);
  if (source) query.append("source", source);
  if (cursor) query.append("cursor", cursor);
  if (limit) query.append("limit", limit);

  const url = `${getApiBase()}/${eventId}/participant/${encodeURIComponent(userId)}/history?${query.toString()}`;
  const res = await fetch(url, { method: "GET", headers: getAuthHeaders() });
  return handleResponse(res);
};

/**
 * Every discrete award across one event, newest first.
 *
 * The event-wide question rather than the per-user one — "who got an adjustment
 * this week", "did the Creator Hub hook double-fire". Keyset-paged: pass the
 * previous response's `next_cursor` to continue.
 */
export const fetchPointsLedger = async (
  eventId,
  { source = "", search = "", cursor = "", limit = 50 } = {}
) => {
  const query = new URLSearchParams();
  if (source) query.append("source", source);
  if (search) query.append("search", search.trim());
  if (cursor) query.append("cursor", cursor);
  if (limit) query.append("limit", limit);

  const url = `${getApiBase()}/${eventId}/points-ledger?${query.toString()}`;
  const res = await fetch(url, { method: "GET", headers: getAuthHeaders() });
  return handleResponse(res);
};

/**
 * Download one participant's history as CSV.
 *
 * Fetched rather than linked because the endpoint needs the bearer token, which a
 * plain anchor cannot send. The object URL is revoked once the click is done, so
 * the blob does not sit in memory for the rest of the session.
 */
export const downloadParticipantHistoryCsv = async (eventId, userId, username = "user") => {
  const url = `${getApiBase()}/${eventId}/participant/${encodeURIComponent(userId)}/history.csv`;
  const res = await fetch(url, { method: "GET", headers: getAuthHeaders() });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data?.message || "Could not export this history");
  }

  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.download = `${String(username).replace(/^@/, "")}-points-history.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(href);
};
